// src/stage2/sceneExterior/ParkingBackdrop.jsx
import React, { useEffect } from 'react';
import { useTexture } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

/** small helper exactly like your chain-gate makeLine */
function makeImageLine({
  start = [0, 0, 0],
  count = 5,
  tileWidth = 10,
  spacing = 0,
  axis = 'x',
  rotationY = 0,
}) {
  const [sx, sy, sz] = start;
  const step = tileWidth + spacing;
  const data = [];

  for (let i = 0; i < count; i++) {
    const offset = i * step;
    let pos;

    if (axis === 'x') pos = [sx + offset, sy, sz];
    else if (axis === 'z') pos = [sx, sy, sz + offset];
    else pos = [sx, sy, sz];

    data.push({
      id: `bg-${axis}-${i}`,
      position: pos,
      rotation: [0, rotationY, 0],
    });
  }

  return data;
}

export function ParkingBackdrop({
  x = -40,
  y = 3,
  z = -2,
  width = 33,
  height = 6,
  rotationY = 1.5,
  foregroundUrl = '/stageAssets/stage2/scene/parkingImage.png',

  bgStart = [-50.5, 3, -50],
  bgWidth = 15.5,
  bgHeight = 6,
  bgCount = 8,
  bgSpacing = -0.5,
  bgAxis = 'z',
  bgRotationY = 1.58,
  backgroundUrl = '/stageAssets/stage2/scene/parkingImageBkgd.png',
}) {
  const { gl } = useThree();
  const [fgTexture, bgTexture] = useTexture([foregroundUrl, backgroundUrl]);

  useEffect(() => {
    const maxAniso = gl.capabilities.getMaxAnisotropy();

    // Helper to stabilize angled textures
    const prep = (tex) => {
      if (!tex) return;

      // If these are “flat images” (not tiling textures), Clamp is safest
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;

      // Big one: reduces shimmering/ripples at glancing angles
      tex.anisotropy = maxAniso;

      // Good defaults for stability
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;

      // If you STILL see crawling, try this (costs a bit of sharpness):
      // tex.generateMipmaps = false;
      // tex.minFilter = THREE.LinearFilter;

      tex.needsUpdate = true;
    };

    prep(fgTexture);
    prep(bgTexture);
  }, [gl, fgTexture, bgTexture]);

  const bgSegments = makeImageLine({
    start: bgStart,
    count: bgCount,
    tileWidth: bgWidth,
    spacing: bgSpacing,
    axis: bgAxis,
    rotationY: bgRotationY,
  });

  return (
    <group>
      {/* Background treeline tiles */}
      {bgSegments.map((seg) => (
        <mesh
          key={seg.id}
          position={seg.position}
          rotation={seg.rotation}
          renderOrder={-10} // draw consistently "behind" most things
        >
          <planeGeometry args={[bgWidth, bgHeight]} />
          <meshBasicMaterial
            map={bgTexture}
            transparent
            depthWrite={false}
            depthTest={true}
            alphaTest={0.5}     // IMPORTANT: try 0.35–0.7 if needed
            toneMapped={false}  // keeps it from looking like it’s “changing” under lighting/tone mapping
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}

      {/* Single foreground parking lot */}
      <mesh
        position={[x, y, z]}
        rotation={[0, rotationY, 0]}
        renderOrder={-5}
      >
        <planeGeometry args={[width, height]} />
        <meshBasicMaterial
          map={fgTexture}
          transparent
          depthWrite={false}   // CHANGE: was true
          depthTest={true}
          alphaTest={0.5}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}
